/**
 * NotesModal.jsx — Post-Call Notes Modal
 *
 * Shown automatically when a call ends.
 * The agent MUST enter notes before the contact can be closed.
 *
 * Props:
 *   isOpen        {boolean}  — whether the modal is visible
 *   dialedNumber  {string}   — the number that was called
 *   finalDuration {number}   — call duration in seconds
 *   onSubmit      {(notes: string) => void} — called with note text on submit
 */

import React, { useState, useRef, useEffect } from "react";
import "./NotesModal.css";

const fmtTime = (s) =>
  `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

export default function NotesModal({ isOpen, dialedNumber, finalDuration, onSubmit }) {
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const [callStatus, setCallStatus] = useState(null);
  const [statusError, setStatusError] = useState(false);
  const textareaRef = useRef(null);

  const statusRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (statusRef.current && !statusRef.current.contains(e.target)) {
        setStatusOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);
  const STATUS_OPTIONS = [
    { value: "Unable to contact", label: "Unable to contact", color: "#E24B4A" },
    { value: "Left voice message", label: "Left voice message", color: "#EF9F27" },
    { value: "Spoke to person", label: "Spoke to person", color: "#639922" },
  ];
  /* Auto-focus textarea when modal opens */
  useEffect(() => {
    if (isOpen) {
      setNotes("");
      setSubmitting(false);
      setTimeout(() => textareaRef.current?.focus(), 80);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    if (!callStatus) {
      setStatusError(true);
      return;
    }
    if (!notes.trim() || submitting) return;

    setStatusError(false);
    setSubmitting(true);
    try {
      await onSubmit(notes.trim(), callStatus);
    } finally {
      setSubmitting(false);
    }
  };
  const handleKeyDown = (e) => {
    /* Ctrl/Cmd + Enter submits */
    if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    /* Backdrop — clicking it does NOTHING (notes are mandatory) */
    <div className="notes-overlay" role="dialog" aria-modal="true">
      <div className="notes-modal" onClick={(e) => e.stopPropagation()}>

        {/* ── Header ── */}
        <div className="notes-header">
          <div className="notes-header-row">
            <div className="notes-header-left">
              <div className="notes-acw-badge">
                <span className="notes-acw-dot" />
                After Call Work
              </div>
              <h3 className="notes-title">Call Summary &amp; Notes</h3>
              <p className="notes-subtitle">
                Notes are <strong>required</strong>.
              </p>
            </div>

            <div className="notes-summary-status" ref={statusRef}>
              <span className="notes-summary-status-label">Call Status</span>
              <button
                className={`notes-summary-status-btn ${statusError ? "notes-summary-status-btn--error" : ""}`}
                onClick={() => setStatusOpen((o) => !o)}
                aria-haspopup="listbox"
                aria-expanded={statusOpen}
                aria-invalid={statusError}
              >
                <span
                  className="notes-summary-status-dot"
                  style={{ background: callStatus?.color ?? "var(--color-border-secondary)" }}
                />
                <span>{callStatus?.label ?? "Select status"}</span>
                <i className="ti ti-chevron-down" aria-hidden="true" />
              </button>

              {statusError && (
                <span className="notes-summary-status-error">
                  <i className="ti ti-alert-circle" aria-hidden="true" />
                  Please select a call status
                </span>
              )}

              {statusOpen && (
                <div className="notes-summary-status-dropdown" role="listbox">
                  <div
                    className="notes-summary-status-option notes-summary-status-option--placeholder"
                    onClick={() => { setCallStatus(null); setStatusOpen(false); }}
                  >
                    <span className="notes-summary-status-dot" style={{ background: "var(--color-border-secondary)" }} />
                    Select
                  </div>
                  {STATUS_OPTIONS.map((opt) => (
                    <div
                      key={opt.value}
                      className="notes-summary-status-option"
                      role="option"
                      aria-selected={callStatus?.value === opt.value}
                      onClick={() => { setCallStatus(opt); setStatusOpen(false); }}
                    >
                      <span className="notes-summary-status-dot" style={{ background: opt.color }} />
                      {opt.label}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Call summary strip ── */}
        <div className="notes-summary-strip">
          <div className="notes-summary-item">
            <span className="notes-summary-item-label">Called To</span>
            <span className="notes-summary-item-value">{dialedNumber || "—"}</span>


          </div>
          <div className="notes-summary-divider" />
          {/* <div className="notes-summary-item notes-summary-item--right">
            <span className="notes-summary-item-label">Duration</span>
            <span className="notes-summary-item-value notes-summary-item-value--lg">
              {fmtTime(finalDuration || 0)}
            </span>
          </div> */}

          <div className="notes-summary-spacer" />


        </div>
        {/* ── Textarea ── */}
        <div className="notes-field-wrap">
          <label className="notes-field-label" htmlFor="notes-textarea">
            Notes
            <span className="notes-char-count">
              {notes.length} / 1000
            </span>
          </label>
          <textarea
            id="notes-textarea"
            ref={textareaRef}
            className={`notes-textarea${!notes.trim() ? " notes-textarea--empty" : ""}`}
            placeholder=""
            value={notes}
            maxLength={1000}
            onChange={(e) => setNotes(e.target.value)}
            onKeyDown={handleKeyDown}
          />
        </div>

        {/* ── Actions ── */}
        <div className="notes-actions">
          <button
            className="notes-btn-clear"
            onClick={() => setNotes("")}
            disabled={!notes || submitting}
          >
            Clear
          </button>
          <button
            className="notes-btn-submit"
            onClick={handleSubmit}
            disabled={!notes.trim() || submitting}
          >
            {submitting ? (
              <span className="notes-submit-spinner" />
            ) : (
              <>
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path
                    d="M2 7.5L5.5 11L12 3"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Add Note
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
